import { useState } from "react";
import { useProducts, Product } from "@/contexts/ProductContext";
import { useAuth, hasPermission } from "@/contexts/AuthContext";
import { apiRequest } from "@/lib/api";
import { Package, Search, Loader2, Trash2, Link, X, Globe } from "lucide-react";
import "./Products.css";

export default function Products() {
  const { products, addProducts, removeProduct, clearProducts } = useProducts();
  const { user } = useAuth();
  const canImport = hasPermission(user?.role, "products:import");
  const canClear = hasPermission(user?.role, "products:clear");
  const [scrapeUrl, setScrapeUrl] = useState("");
  const [isScraping, setIsScraping] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [error, setError] = useState("");

  const categories = ["all", ...Array.from(new Set(products.map((p) => p.category).filter(Boolean)))];

  const filtered = products.filter((p) => {
    if (selectedCategory !== "all" && p.category !== selectedCategory) return false;
    if (search) {
      const q = search.toLowerCase();
      return p.name.toLowerCase().includes(q) || p.category?.toLowerCase().includes(q);
    }
    return true;
  });

  const scrape = async () => {
    if (!scrapeUrl) return;
    setIsScraping(true);
    setError("");
    try {
      const data = await apiRequest<{ products: Product[] }>("/products/scrape", {
        method: "POST",
        body: JSON.stringify({ url: scrapeUrl }),
      });
      addProducts(data.products || []);
      setScrapeUrl("");
    } catch (err: any) {
      setError(err.message);
    }
    setIsScraping(false);
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="header-row">
          <div>
            <h1>Product Catalog</h1>
            <p>{products.length} products in catalog</p>
          </div>
          {products.length > 0 && canClear && (
            <button className="btn btn-danger" onClick={() => { if (confirm("Clear all products?")) clearProducts(); }}>
              <Trash2 size={14} /> Clear All
            </button>
          )}
        </div>
      </div>

      {canImport && (
        <div className="card scrape-card">
          <h3><Globe size={16} /> Import Products from Website</h3>
          <div className="url-input-row" style={{ marginTop: 12 }}>
            <input placeholder="Enter furniture store website URL..." value={scrapeUrl}
              onChange={(e) => setScrapeUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && scrape()} />
            <button className="btn btn-primary" onClick={scrape} disabled={isScraping || !scrapeUrl}>
              {isScraping ? <><Loader2 size={16} className="spinning" /> Scraping...</> : <><Link size={16} /> Import</>}
            </button>
          </div>
          {error && <div className="login-error" style={{ marginTop: 12 }}>{error}</div>}
        </div>
      )}

      {products.length > 0 && (
        <div className="card" style={{ marginTop: 20 }}>
          <div className="filters-row">
            <div className="search-box">
              <Search size={16} />
              <input placeholder="Search products..." value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div className="status-filters">
              {categories.map((cat) => (
                <button key={cat} className={`filter-chip ${selectedCategory === cat ? "active" : ""}`}
                  onClick={() => setSelectedCategory(cat)}>
                  {cat === "all" ? "All" : cat}
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="empty-state"><h3>No products found</h3></div>
          ) : (
            <div className="products-grid">
              {filtered.map((p) => (
                <div key={p.id} className="product-card">
                  {p.imageUrl ? (
                    <img src={p.imageUrl} alt={p.name} className="product-image" />
                  ) : (
                    <div className="product-image-placeholder"><Package size={32} /></div>
                  )}
                  <div className="product-info">
                    <h4>{p.name}</h4>
                    <span className="product-price">{p.price}</span>
                    {p.category && <span className="product-category">{p.category}</span>}
                    {p.description && <p className="product-desc">{p.description}</p>}
                  </div>
                  {canClear && (
                    <button className="icon-btn danger product-remove" onClick={() => removeProduct(p.id)} title="Remove">
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {products.length === 0 && !isScraping && (
        <div className="card" style={{ marginTop: 20 }}>
          <div className="empty-state">
            <Package size={40} style={{ marginBottom: 12, color: "var(--primary)" }} />
            <h3>No Products Yet</h3>
            <p>Import products from a furniture store website using the URL above</p>
          </div>
        </div>
      )}
    </div>
  );
}
